import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";

import {
  AiExtractionStateService,
  AiFieldProvenance,
} from "src/app/core/services/ai-extraction-state.service";

import { sameControlValue } from "./ai-extraction-patch.util";

/** Objetos partilhados entre o formulário e os separadores (`{ data }`). */
export interface AiProvenanceSources {
  details?: { data?: any };
  address?: { data?: any };
  areasNearby?: { data?: any };
}

const BADGE_ATTRIBUTE = "data-ai-provenance";
const REFRESH_DELAY_MS = 400;

/**
 * Badge "IA" junto dos campos preenchidos pela extração de documentos.
 *
 * Fica no contentor dos separadores do formulário do imóvel e decora os
 * controlos pelo `formControlName`, para não ser preciso tocar no template de
 * cada separador. O badge só existe enquanto o controlo tiver o valor que a
 * extração lá pôs: se o utilizador o alterar, desaparece.
 *
 * Sem proveniência não faz nada — é o caso de quem não usa o serviço.
 */
@Directive({ selector: "[appAiProvenance]" })
export class AiProvenanceDirective implements OnInit, OnDestroy {
  @Input("appAiProvenance") sources: AiProvenanceSources | null = null;

  private fields: AiFieldProvenance[] = [];
  private subscription?: Subscription;
  private observer?: MutationObserver;
  private timer: any = null;
  private listening = false;
  private readonly onUserInput = () => this.schedule();

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private state: AiExtractionStateService,
    private translate: TranslateService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.subscription = this.state.observeProvenance().subscribe((fields) => {
      this.fields = fields ?? [];
      if (this.fields.length) this.watch();
      this.schedule();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.observer?.disconnect();
    if (this.timer) clearTimeout(this.timer);
    if (this.listening) {
      this.host.nativeElement.removeEventListener("input", this.onUserInput);
      this.host.nativeElement.removeEventListener("change", this.onUserInput);
    }
  }

  /**
   * Só se observa o formulário quando há alguma coisa para decorar, e sempre
   * fora da zona do Angular: sem proveniência esta diretiva não regista
   * nenhum listener nem provoca nenhuma deteção de alterações.
   */
  private watch() {
    if (this.observer || typeof MutationObserver === "undefined") return;
    this.zone.runOutsideAngular(() => {
      this.listening = true;
      this.host.nativeElement.addEventListener("input", this.onUserInput);
      this.host.nativeElement.addEventListener("change", this.onUserInput);
      this.observer = new MutationObserver((mutations) => {
        const relevant = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              !(node as HTMLElement).hasAttribute?.(BADGE_ATTRIBUTE),
          ),
        );
        if (relevant) this.schedule();
      });
      this.observer.observe(this.host.nativeElement, {
        childList: true,
        subtree: true,
      });
    });
  }

  private schedule() {
    if (this.timer) clearTimeout(this.timer);
    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => this.refresh(), REFRESH_DELAY_MS);
    });
  }

  private currentValue(field: AiFieldProvenance): any {
    const source = (this.sources as any)?.[field.tab]?.data;
    if (!source) return undefined;
    const value = source[field.control];
    // Antes da primeira alteração, os separadores guardam `[valor, validadores]`.
    return Array.isArray(value) ? value[0] : value;
  }

  private refresh() {
    const root = this.host.nativeElement;
    const wanted = new Map<string, AiFieldProvenance>();

    for (const field of this.fields) {
      if (!sameControlValue(this.currentValue(field), field.value)) continue;
      wanted.set(field.control, field);
    }

    const existing = Array.from(
      root.querySelectorAll<HTMLElement>(`[${BADGE_ATTRIBUTE}]`),
    );
    for (const badge of existing) {
      const control = badge.getAttribute(BADGE_ATTRIBUTE) ?? "";
      if (!wanted.has(control)) badge.remove();
    }

    wanted.forEach((field, control) => {
      if (root.querySelector(`[${BADGE_ATTRIBUTE}="${control}"]`)) return;
      const element = root.querySelector<HTMLElement>(
        `[formcontrolname="${control}"]`,
      );
      if (!element) return;
      this.decorate(element, field);
    });
  }

  private decorate(element: HTMLElement, field: AiFieldProvenance) {
    const badge: HTMLElement = this.renderer.createElement("span");
    this.renderer.setAttribute(badge, BADGE_ATTRIBUTE, field.control);
    this.renderer.addClass(badge, "badge");
    this.renderer.addClass(badge, "badge-soft-primary");
    this.renderer.setStyle(badge, "margin-left", "0.375rem");
    this.renderer.setStyle(badge, "font-size", "0.625rem");
    this.renderer.setStyle(badge, "vertical-align", "middle");
    this.renderer.setStyle(badge, "cursor", "help");
    this.renderer.setAttribute(badge, "title", this.tooltip(field));
    this.renderer.appendChild(
      badge,
      this.renderer.createText(this.translate.instant("ai-extraction.badge.label")),
    );

    const group = element.closest(
      ".mb-3, .mb-2, .form-group, [class*='col-']",
    ) as HTMLElement | null;
    const label = group?.querySelector("label");
    if (label) {
      this.renderer.appendChild(label, badge);
      return;
    }
    const parent = element.parentElement;
    if (parent) this.renderer.insertBefore(parent, badge, element);
  }

  private tooltip(field: AiFieldProvenance): string {
    const parts = [this.translate.instant("ai-extraction.badge.tooltip")];
    if (field.documentName) parts.push(field.documentName);
    if (field.page) {
      parts.push(`${this.translate.instant("ai-extraction.review.page")} ${field.page}`);
    }
    if (field.confidence !== null && field.confidence !== undefined) {
      parts.push(`${Math.round(Number(field.confidence) * 100)}%`);
    }
    return parts.join(" · ");
  }
}
